import { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { getUnitPricingTier, requestAdditionalUnits } from '../../../api/settings';
import { StoreContext } from '../../../components/Store';
import PricingTiersTable from './PricingTiersTable';

const calculateTierAndCost = (tiers, quantity, currency) => {
  const qty = parseInt(quantity);
  const tier = tiers.find((tr) => qty >= tr.declarationMin && (!tr.declarationMax || qty <= tr.declarationMax));
  const price = currency === 'EUR' ? tier?.priceEur : tier?.priceGbp;
  const cost = price ? (qty * price).toFixed(2) : 0;
  return cost;
};

const InvoiceNote = () => {
  return (
    <Box sx={{ my: 4 }}>
      Please note: Invoices are raised Monday-Friday between 09:00 and 17:00 (GMT/BST).
      <br />
      Your units balance will be updated once the invoice has been paid.
    </Box>
  );
};

const RequestUnitsModal = ({ isOpen, setIsOpen, companyId }) => {
  const store = useContext(StoreContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const [pricingTiers, setPricingTiers] = useState([]);
  const [requestDetails, setRequestDetails] = useState({
    currency: 'GBP',
    quantity: 0,
  });

  const [currentCost, setCurrentCost] = useState(0);

  const GENERIC_ERROR = 'An unknown error occurred. Please try again later.';

  // initiailize the modal once it has opened
  useEffect(() => {
    const fetchPricingTiers = async () => {
      try {
        const data = await getUnitPricingTier();
        setPricingTiers(data);
      } catch (err) {
        setError(GENERIC_ERROR);
        store.showError('Fetching pricing tiers. ' + err.message);
      }
    };

    if (isOpen && !pricingTiers.length) {
      fetchPricingTiers();
    }

    setIsSubmitting(false);
    setIsSuccess(false);
    setError('');
    setRequestDetails({
      currency: 'GBP',
      quantity: 0,
    });
  }, [isOpen]);

  // calculate the tier pricing based on the quantity entered by the user
  useEffect(() => {
    if (pricingTiers.length) {
      const cost = calculateTierAndCost(pricingTiers, requestDetails.quantity, requestDetails.currency);
      setCurrentCost(cost);
    }
  }, [requestDetails.quantity, requestDetails.currency, pricingTiers]);

  // handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = name === 'quantity' ? value.replace(/\D/g, '').replace(/^0+/, '') : value;
    setRequestDetails((prev) => ({ ...prev, [name]: newValue }));
  };

  // handle submitting the form
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const data = await requestAdditionalUnits({ ...requestDetails, companyId });
      if (data.quantity > 0 && data.cost > 0) {
        setIsSuccess(true);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(GENERIC_ERROR);
      store.showError('Submitting request. ' + err.message);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => !isSubmitting && setIsOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          p: 4,
          borderRadius: 5,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Request Additional Units
        </Typography>

        {isSuccess ? (
          <>
            <Alert sx={{ marginTop: 2, marginBottom: 4 }}>
              Your request for additional units has been sent successfully.
            </Alert>
            <InvoiceNote />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span></span>
              <Button variant="contained" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            {error && (
              <Box sx={{ paddingBottom: 4 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <Box sx={{ mt: 2, mb: 4 }}>
              <PricingTiersTable tiers={pricingTiers} />
            </Box>

            <Typography id="modal-modal-title" variant="h6" component="h3">
              Request Invoice:
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
              <Box sx={{ width: 150 }}>Currency</Box>
              <Box sx={{ flex: 1 }}>
                <FormControl>
                  <RadioGroup row name="currency" value={requestDetails.currency} onChange={handleChange}>
                    <FormControlLabel value="GBP" label="GBP" control={<Radio />} disabled={isSubmitting} />
                    <FormControlLabel value="EUR" label="EUR" control={<Radio />} disabled={isSubmitting} />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: 150 }}>Quantity</Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  name="quantity"
                  value={requestDetails.quantity}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  fullWidth
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: 150 }}>Cost</Box>
              <Box sx={{ flex: 1 }}>
                <TextField id="outlined-basic" variant="outlined" name="cost" value={currentCost} disabled fullWidth />
              </Box>
            </Box>

            <InvoiceNote />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>

              <Button
                variant="contained"
                loading={isSubmitting}
                onClick={handleSubmit}
                disabled={!(requestDetails.quantity > 0 && currentCost > 0) || isSubmitting}
              >
                Request
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RequestUnitsModal;
